import React, { useRef } from 'react'
import cn from 'classnames'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import Splash from '../../components/Splash'
import { ICycleCountItem } from '../../types/cycle-counts'
import { money } from '../../common/display'

const TABLE_WIDTHS = {
  CHECKBOX: '-fix100',
  STATUS: '-g8',
  ITEM: '-g8',
  DESCRIPTION: '-g20',
  UNIT: '-fix100',
  LOCATION_AMOUNT: '-fix150',
  COUNTED_AMOUNT: '-fix150',
  VARIANCE: '-fix150',
  TOTAL_VARIANCE: '-fix150',
}

const ITEM_STATUS_MAP = {
  pending: '-',
  approved: 'Approved',
  rejected: 'Recount',
}

const ItemRow: React.FC<ListChildComponentProps> = ({ index, style, data }) => {
  const item: ICycleCountItem = data.itemList[index]
  const code = item.product.code
  const description = item.product.name
  const { price, unit } = item
  const uom = price && unit ? `${money(price)} / ${unit}` : unit || ''
  const isApproved = item.status === 'approved'

  return (
    <div style={style}>
      <div
        className="table_tr -fixed-height -link"
        onClick={() => !isApproved && data.onClick(item)}
      >
        {!data.isCycleCountCompleted && !isApproved && (
          <div className={`table_td ${TABLE_WIDTHS.CHECKBOX}`}>
            <input
              type="checkbox"
              checked={data.isSelected(item)}
              onChange={() => data.onClick(item)}
            />
          </div>
        )}
        {!data.isCycleCountCompleted && isApproved && (
          <div className={`table_td ${TABLE_WIDTHS.CHECKBOX}`}></div>
        )}
        <div className={`table_td ${TABLE_WIDTHS.STATUS}`}>
          <div
            style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
            className={cn({
              'user-status_badge': item.status !== 'pending',
              active: item.status === 'approved',
              inactive: item.status === 'rejected',
            })}
          >
            {ITEM_STATUS_MAP[item.status]}
          </div>
        </div>
        <div className={`table_td ${TABLE_WIDTHS.ITEM}`}>{code}</div>
        <div className={`table_td ${TABLE_WIDTHS.DESCRIPTION}`}>
          {description}
        </div>
        <div className={`table_td ${TABLE_WIDTHS.UNIT}`}>{uom}</div>
        <div className={`table_td ${TABLE_WIDTHS.LOCATION_AMOUNT}`}>
          {item.locationAmount}
        </div>
        <div className={`table_td ${TABLE_WIDTHS.COUNTED_AMOUNT}`}>
          {item.countedAmount}
        </div>
        <div className={`table_td ${TABLE_WIDTHS.VARIANCE}`}>
          {item.variance}
        </div>
        <div className={`table_td ${TABLE_WIDTHS.TOTAL_VARIANCE}`}>
          {item.totalVariancePrice >= 0
            ? money(item.totalVariancePrice)
            : `-${money(Math.abs(item.totalVariancePrice))}`}
        </div>
      </div>
    </div>
  )
}

interface ICycleCountApproveTableProps {
  itemList: ICycleCountItem[]
  loading: boolean
  selectedItems: ICycleCountItem[]
  onItemSelect: (item: ICycleCountItem) => void
  toggleAllItems: () => void
  isCompleted: boolean
}

const CycleCountApproveTable: React.FC<ICycleCountApproveTableProps> = ({
  itemList,
  loading,
  selectedItems,
  onItemSelect,
  toggleAllItems,
  isCompleted,
}) => {
  const listRef = useRef<FixedSizeList>(null)
  const listStyles = { height: 'calc(100vh - 300px)' }
  const selectableItems = itemList.filter((item) => item.status !== 'approved')
  const allSelectableSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedItems.includes(item))

  return (
    <>
      <div className="table">
        <div className="table_head-tr -fixed-height">
          {!isCompleted && (
            <div className={`table_td table_title ${TABLE_WIDTHS.CHECKBOX}`}>
              <input
                type="checkbox"
                checked={allSelectableSelected}
                onChange={toggleAllItems}
                disabled={selectableItems.length === 0}
              />
            </div>
          )}
          <div className={`table_td table_title ${TABLE_WIDTHS.STATUS}`}>
            Status
          </div>
          <div className={`table_td table_title ${TABLE_WIDTHS.ITEM}`}>
            Item
          </div>
          <div className={`table_td table_title ${TABLE_WIDTHS.DESCRIPTION}`}>
            Description
          </div>
          <div className={`table_td table_title ${TABLE_WIDTHS.UNIT}`}>
            Unit
          </div>
          <div
            className={`table_td table_title ${TABLE_WIDTHS.LOCATION_AMOUNT}`}
          >
            Location Amount
          </div>
          <div
            className={`table_td table_title ${TABLE_WIDTHS.COUNTED_AMOUNT}`}
          >
            Counted Amount
          </div>
          <div className={`table_td table_title ${TABLE_WIDTHS.VARIANCE}`}>
            Variance
          </div>
          <div
            className={`table_td table_title ${TABLE_WIDTHS.TOTAL_VARIANCE}`}
          >
            Total Variance
          </div>
        </div>
      </div>

      <div className="table_loading-wrapper">
        <AutoSizer className="table" style={listStyles}>
          {(size) => (
            <FixedSizeList
              ref={listRef}
              className="wonder-scroll"
              {...size}
              itemData={{
                itemList: [...itemList].sort((a, b) => {
                  const aCode = a.product.code
                  const bCode = b.product.code
                  const codeCompare = aCode.localeCompare(bCode)
                  if (codeCompare !== 0) return codeCompare
                  return a.id - b.id
                }),
                onClick: (item: ICycleCountItem) => {
                  onItemSelect(item)
                },
                isSelected: (item: ICycleCountItem) => {
                  return selectedItems.includes(item)
                },
                isCycleCountCompleted: isCompleted,
              }}
              itemCount={itemList.length}
              itemSize={55 + 17}
              itemKey={(index) => itemList[index].id.toString()}
            >
              {ItemRow}
            </FixedSizeList>
          )}
        </AutoSizer>
        <Splash isLoading={loading} bgColor="#F2F6FA" />
      </div>
    </>
  )
}

export default CycleCountApproveTable
